import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/** Durée d'affichage du message d'erreur d'ajout (ms). */
const ERROR_DISPLAY_DURATION = 8000;

/**
 * Bloc « Ceci pourrait vous plaire » du tiroir panier.
 *
 * Le HTML (cartes, prix, variantes) est rendu côté serveur par
 * `snippets/cart-drawer-recommendations.liquid` : après chaque changement du panier le tiroir est
 * re-rendu (Section Rendering API) puis « morphé », donc les suggestions se recalculent toutes seules.
 * Ce composant ne gère que le comportement :
 * - flèches ‹ › qui font défiler la piste d'une carte ;
 * - changement de variante dans le <select> d'une carte (le prix suit la variante) ;
 * - bouton « + » : ajout d'une unité au panier, en passant par les mêmes événements que le reste du
 *   thème (CartLinesUpdateEvent) pour que les lignes, le total, la pastille de l'en-tête du site et les
 *   suggestions se mettent à jour sans recharger la page.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - La piste défilante des cartes.
 * @property {HTMLElement} [navigation] - Le conteneur des flèches.
 * @property {HTMLButtonElement} [previousButton] - La flèche précédente.
 * @property {HTMLButtonElement} [nextButton] - La flèche suivante.
 * @property {HTMLElement} [error] - Le message d'erreur d'ajout.
 * @property {HTMLElement} [liveRegion] - La zone d'annonce pour les lecteurs d'écran.
 *
 * @extends {Component<Refs>}
 */
class CartRecommendationsComponent extends Component {
  requiredRefs = ['track'];

  /** @type {HTMLElement | null} */
  #observedTrack = null;

  /** Identifiants des cartes affichées, pour savoir si la liste a changé après un re-rendu du tiroir. */
  /** @type {string[]} */
  #cardIds = [];

  /** @type {ResizeObserver} */
  #resizeObserver = new ResizeObserver(() => this.#syncNavigation());

  /**
   * Le thème « morphe » le tiroir après chaque changement du panier : les flèches (attribut `disabled`)
   * reviennent à l'état rendu par le serveur et les cartes peuvent avoir changé.
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(() => this.#onMutations());

  /** @type {number} */
  #frame = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #errorTimeout;

  /**
   * Message d'erreur d'ajout en cours d'affichage. Après une erreur, le thème re-rend le tiroir (le HTML du
   * serveur remet le message caché) : on le réaffiche donc à chaque re-rendu tant qu'il doit rester visible.
   */
  #errorMessage = '';
  #errorUntil = 0;
  #defaultErrorText = '';

  /** @type {boolean} */
  #adding = false;

  connectedCallback() {
    super.connectedCallback();
    this.#defaultErrorText = this.#ref('error')?.textContent?.trim() ?? '';
    this.#bindTrack();
    this.#mutationObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled', 'hidden'],
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#onMutations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mutationObserver.disconnect();
    this.#unbindTrack();
    clearTimeout(this.#errorTimeout);
  }

  /* ------------------------------------------------------------------ */
  /* Accès directs au DOM (les refs peuvent avoir une longueur de retard */
  /* juste après un morph)                                              */
  /* ------------------------------------------------------------------ */

  /** @param {string} name */
  #ref(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[ref="${name}"]`));
  }

  /** @returns {string[]} */
  #readCardIds() {
    return Array.from(this.querySelectorAll('.cart-reco__card'), (card) => card.id);
  }

  /* ------------------------------------------------------------------ */
  /* Flèches et défilement                                              */
  /* ------------------------------------------------------------------ */

  #bindTrack() {
    const track = this.#ref('track');

    if (track && track !== this.#observedTrack) {
      this.#unbindTrack();
      this.#observedTrack = track;
      this.#cardIds = this.#readCardIds();
      track.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#resizeObserver.observe(track);
    }

    this.#syncNavigation();
  }

  #unbindTrack() {
    if (!this.#observedTrack) return;

    this.#observedTrack.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver.unobserve(this.#observedTrack);
    this.#observedTrack = null;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #onScroll = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#syncNavigation();
    });
  };

  /**
   * Après un re-rendu du tiroir : si les suggestions ne sont pas simplement les mêmes moins la carte qu'on
   * vient d'ajouter (ex. le type du premier article a changé, donc d'autres produits sont proposés), la
   * piste repart du début. Sinon la position de défilement est conservée.
   */
  #onMutations() {
    this.#bindTrack();

    const track = this.#observedTrack;
    const ids = this.#readCardIds();
    const onlyRemovals = ids.every((id) => this.#cardIds.includes(id));

    if (track && !onlyRemovals) track.scrollTo({ left: 0, behavior: 'instant' });

    this.#cardIds = ids;
    this.#syncNavigation();
    this.#renderError();
    // Nos propres changements (disabled / hidden / texte) ne doivent pas relancer cette méthode.
    this.#mutationObserver.takeRecords();
  }

  /**
   * Active / désactive les flèches selon la position de la piste, et les masque quand tout tient à l'écran.
   * Quand le tiroir est fermé (largeur 0), on ne touche à rien : l'état rendu par le serveur reste valable.
   */
  #syncNavigation() {
    const track = this.#observedTrack;

    if (!track || track.clientWidth === 0) return;

    const navigation = this.#ref('navigation');
    const previousButton = /** @type {HTMLButtonElement | null} */ (this.#ref('previousButton'));
    const nextButton = /** @type {HTMLButtonElement | null} */ (this.#ref('nextButton'));
    const maxScroll = track.scrollWidth - track.clientWidth;

    if (navigation) navigation.hidden = maxScroll <= 2;
    if (previousButton) previousButton.disabled = track.scrollLeft <= 1;
    if (nextButton) nextButton.disabled = track.scrollLeft >= maxScroll - 1;
  }

  /** @param {number} direction - -1 pour reculer, 1 pour avancer d'une carte. */
  #scrollByCard(direction) {
    const track = this.#observedTrack;
    const card = track?.querySelector('.cart-reco__card');

    if (!track || !(card instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;

    track.scrollBy({
      left: direction * (card.offsetWidth + gap),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  scrollPrevious = () => {
    this.#scrollByCard(-1);
  };

  scrollNext = () => {
    this.#scrollByCard(1);
  };

  /* ------------------------------------------------------------------ */
  /* Variantes                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Un <select> de carte a changé : le prix affiché (et l'ancien prix barré) suit la variante choisie.
   * @param {Event} event
   */
  selectVariant = (event) => {
    const select = event.target;

    if (!(select instanceof HTMLSelectElement)) return;

    const card = select.closest('.cart-reco__card');
    const option = select.selectedOptions[0];

    if (!card || !option) return;

    const price = card.querySelector('[data-reco-price]');
    const compareWrapper = card.querySelector('.cart-reco__compare');
    const compare = card.querySelector('[data-reco-compare]');
    const comparePrice = option.dataset.comparePrice ?? '';

    if (price) price.textContent = option.dataset.price ?? '';
    if (compare) compare.textContent = comparePrice;
    if (compareWrapper instanceof HTMLElement) compareWrapper.hidden = comparePrice === '';

    const addButton = card.querySelector('.cart-reco__add');
    if (addButton instanceof HTMLElement) addButton.dataset.variantId = option.value;
  };

  /* ------------------------------------------------------------------ */
  /* Ajout au panier                                                    */
  /* ------------------------------------------------------------------ */

  /**
   * Récupère le panier complet (même méthode que le formulaire produit du thème).
   * @returns {Promise<any>}
   */
  async #fetchCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

    return response.json();
  }

  /** @param {string} [message] - Message précis de Shopify ; à défaut, le message par défaut (rendu par le serveur). */
  #showError(message) {
    if (!this.#ref('error')) return;

    this.#errorMessage = message || this.#defaultErrorText;
    this.#errorUntil = Date.now() + ERROR_DISPLAY_DURATION;
    this.#renderError();

    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => this.#hideError(), ERROR_DISPLAY_DURATION);
  }

  #hideError() {
    clearTimeout(this.#errorTimeout);
    this.#errorUntil = 0;
    this.#renderError();
  }

  /** Aligne le message affiché sur l'état voulu (sans rien écrire s'il est déjà à jour). */
  #renderError() {
    const error = this.#ref('error');

    if (!error) return;

    const active = Date.now() < this.#errorUntil;

    if (active && this.#errorMessage && error.textContent?.trim() !== this.#errorMessage) {
      error.textContent = this.#errorMessage;
    }

    if (error.hidden === active) error.hidden = !active;
  }

  /**
   * Bouton « + » d'une carte : ajoute 1 unité de la variante choisie.
   * @param {Event} event
   */
  addToCart = async (event) => {
    const button = event.target;

    if (!(button instanceof HTMLButtonElement) || this.#adding) return;

    const card = button.closest('.cart-reco__card');

    if (!(card instanceof HTMLElement)) return;

    const select = card.querySelector('.cart-reco__select');
    const variantId = select instanceof HTMLSelectElement ? select.value : button.dataset.variantId;

    if (!variantId) return;

    const productId = card.dataset.productId;
    const addedMessage = card.dataset.addedMessage ?? '';
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    this.#adding = true;
    this.#hideError();
    button.setAttribute('aria-busy', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        // Erreur Shopify (ex. stock insuffisant) : on l'affiche dans le bloc, le panier ne change pas.
        this.#showError(result.description || undefined);

        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );

        const cart = await this.#fetchCart();

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'cart-recommendations-component',
            sourceId: 'cart-recommendations',
            itemCount: 1,
            productId,
          },
        });

        return;
      }

      const cart = await this.#fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-recommendations-component',
          sourceId: 'cart-recommendations',
          itemCount: 1,
          productId,
          sections: result.sections,
          didError: false,
        },
      });

      // Laisse le thème « morpher » le tiroir (lignes, total, suggestions recalculées), puis annonce l'ajout.
      await new Promise((resolve) => setTimeout(resolve, 60));

      const liveRegion = this.#ref('liveRegion');
      if (liveRegion && addedMessage) liveRegion.textContent = addedMessage;

      this.#restoreFocus(button);
    } catch (error) {
      console.error(error);
      deferred.reject(error);

      this.#showError();
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error && error.message ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      button.removeAttribute('aria-busy');
      this.#adding = false;
    }
  };

  /**
   * Après l'ajout, la carte (donc le bouton qui avait le focus) a disparu des suggestions :
   * on remet le focus sur la carte suivante, à défaut sur le bouton de commande.
   * @param {HTMLElement} previousButton
   */
  #restoreFocus(previousButton) {
    const active = document.activeElement;
    const lostFocus = !active || active === document.body || !document.contains(previousButton);

    if (!lostFocus || !document.querySelector('#cart-drawer[open]')) return;

    const next = this.isConnected ? this.querySelector('.cart-reco__add') : null;
    const fallback = document.querySelector('#cart-drawer .cart__checkout-button');
    const target = next instanceof HTMLElement ? next : fallback;

    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define('cart-recommendations-component', CartRecommendationsComponent);
}
